import { useState, useEffect, useCallback, useRef, useMemo, forwardRef } from 'react';
import { useTheme, alpha } from '@mui/material/styles';
import {
  Box, Card, Container, Grid, Button, TextField, Stack, Typography, Chip, IconButton,
  Slider, Divider, Snackbar, Alert, CircularProgress, Skeleton, Autocomplete,
  Dialog, DialogTitle, DialogActions, DialogContent,
  Tooltip, InputAdornment, Paper, Popover, ListItemButton, ListItemText,
  Switch, FormControlLabel, Slide,
  Breadcrumbs,
} from '@mui/material';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CloseIcon from '@mui/icons-material/Close';
import TuneIcon from '@mui/icons-material/Tune';
import SaveIcon from '@mui/icons-material/Save';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import WifiIcon from '@mui/icons-material/Wifi';
import SettingsEthernetIcon from '@mui/icons-material/SettingsEthernet';
import NumbersIcon from '@mui/icons-material/Numbers';
import VideocamOffIcon from '@mui/icons-material/VideocamOff';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Page from '../components/Page';
import DashboardBreadcrumbLink from 'src/components/DashboardBreadcrumbLink';
import cameraModelComboClientConfig from '../config/cameraModelComboClientConfig.json';

const BASE = (process.env.REACT_APP_API || '').replace(/\/$/, '');
const DEVICE_LIMIT = 50;
const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const DAY_NAME_TO_NUM = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
const NUM_TO_DAY_NAME = { 0: "Sun", 1: "Mon", 2: "Tue", 3: "Wed", 4: "Thu", 5: "Fri", 6: "Sat" };

const MODEL_FORMATS = [
  { key: "onnx", label: "ONNX" },
  { key: "xml", label: "OpenVINO" },
  { key: "paddle", label: "PaddlePaddle" },
  { key: "engine", label: "TensorRT" },
  { key: "tflite", label: "TFLite" },
  { key: "pt", label: "PyTorch" },
];

const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const EMPTY_ITEM = {
  id: 0, class_id: null, is_positive: true, min_objects: 1,
  condition_type: "and", order_no: 1, max_distance: 0, status: 1,
};

const EMPTY_COMBO = {
  id: 0, combo_name: "", display_name: "",
  poster_url: "", description: "",
  combo_fps: 5, combo_motion_fps: 1, min_wait_time: 50,
  probability_threshold: 0.8,
  items: [{ ...EMPTY_ITEM }],
};

const DEFAULT_MODEL_CFG = {
  class_id: null,
  model_config_id: 0,
  model_version_id: null,
  model_version_label: "",
  conf: 0.5,
  iou: 0.45,
  detect_fps: 5,
  model_format: "pt",
  agnostic_nms: false,
  is_zoom_motion_area: false,
  communication_type: "grpc",
  grpc_connect_ip: "127.0.0.1",
  grpc_connect_port: 50051,
  http_private_base_url: "",
  http_public_base_url: "",
  http_path: "",
};

const DEFAULT_TRACKING = {
  tracking_config_id: 0,
  track_iou: 0.3,
  track_max_disappeared: 30,
  tracking_fps: 5,
  min_track_distance: 50,
  min_track_duration_sec: 3,
  enable_object_count: false,
  track_direction: "TTB",
  track_line: null,
};

const normalizeComboFromApi = (combo = {}) => {
  const { status, model, model_version, ...rest } = combo;
  return {
    ...rest,
    poster_url: combo.poster_url || "",
    description: combo.description || "",
    items: Array.isArray(combo.items)
      ? combo.items.map(item => ({ ...item, condition_type: (item.condition_type || "and").toLowerCase() }))
      : [],
  };
};

const buildComboPayload = (combo = {}, isNew = false) => {
  const payload = {
    combo_name: combo.combo_name?.trim() || "",
    display_name: combo.display_name?.trim() || "",
    description: combo.description || "",
    combo_fps: Number(combo.combo_fps) || 5,
    combo_motion_fps: Number(combo.combo_motion_fps) || 1,
    min_wait_time: Number(combo.min_wait_time) || 0,
    probability_threshold: Number(combo.probability_threshold) || 0.8,
    items: (combo.items || []).filter(item => item.class_id).map((item, i) => ({
      class_id: Number(item.class_id),
      is_positive: Boolean(item.is_positive),
      min_objects: Number(item.min_objects) || 1,
      condition_type: (item.condition_type || "AND").toUpperCase(),
      order_no: i + 1,
      max_distance: Number(item.max_distance) || 0,
    })),
  };
  if (!isNew && combo.id != null) payload.id = Number(combo.id);
  return payload;
};

export default function Combo() {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const sectionBg = isDark ? alpha(theme.palette.background.paper, 0.32) : theme.palette.grey[50];
  const searchInputBg = isDark ? alpha(theme.palette.background.paper, 0.42) : theme.palette.grey[50];
  const searchInputBorder = isDark ? alpha(theme.palette.common.white, 0.2) : theme.palette.grey[200];
  const listThumbBg = isDark ? alpha(theme.palette.common.white, 0.16) : theme.palette.grey[200];
  const emptyStateBg = isDark ? alpha(theme.palette.common.white, 0.12) : theme.palette.grey[100];
  const softBorder = isDark ? alpha(theme.palette.common.white, 0.22) : theme.palette.grey[300];
  const softDivider = isDark ? alpha(theme.palette.common.white, 0.16) : theme.palette.grey[200];
  const UI = {
    bg: theme.palette.background.default,
    panel: theme.palette.background.paper,
    surface: theme.palette.background.neutral,
    border: alpha(theme.palette.text.primary, isDark ? 0.16 : 0.2),
    textMuted: theme.palette.text.secondary,
    textMain: theme.palette.text.primary,
    accent: theme.palette.primary.main,
    accentSoft: alpha(theme.palette.primary.main, isDark ? 0.18 : 0.12),
    dark: theme.palette.primary.main,
    darkContrast: theme.palette.primary.contrastText,
    green: theme.palette.success.main,
    subtleIcon: alpha(theme.palette.text.primary, isDark ? 0.82 : 0.62),
    inputBg: alpha(theme.palette.text.primary, isDark ? 0.14 : 0.04),
    hoverBg: alpha(theme.palette.text.primary, isDark ? 0.22 : 0.08),
    panelSoft: alpha(theme.palette.text.primary, isDark ? 0.12 : 0.03),
    disabledBg: theme.palette.action.disabledBackground,
    disabledText: theme.palette.text.disabled,
    scrollThumb: alpha(theme.palette.text.primary, isDark ? 0.18 : 0.3),
    scrollThumbHover: alpha(theme.palette.text.primary, isDark ? 0.28 : 0.45),
  };
  const POP_LIST_SX = {
    bgcolor: UI.panel,
    color: UI.textMain,
    borderRadius: 2,
    mt: 0.5,
    boxShadow: `0 12px 28px ${alpha(theme.palette.common.black, 0.2)}`,
  };

  // clients listed in cameraModelComboClientConfig.json get a read-only slice of this page:
  // only their display_combo_ids are listed and creating a combo is not offered
  const currentUserType = Number(localStorage.getItem('user_type'));
  const currentClientId = localStorage.getItem('client_id');
  const clientComboConfig = useMemo(
    () => cameraModelComboClientConfig.find(config => Number(config.client_id) === Number(currentClientId)),
    [currentClientId]
  );
  const isComboRestricted = currentUserType !== 9 && Boolean(clientComboConfig);
  const allowedComboIds = useMemo(
    () => (clientComboConfig?.display_combo_ids || []).map(Number),
    [clientComboConfig]
  );

  const [combos, setCombos] = useState([]);
  const [classOptions, setClassOptions] = useState([]);
  const posterInputRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [posterFile, setPosterFile] = useState(null);
  const [posterPreviewUrl, setPosterPreviewUrl] = useState("");
  const [posterDragOver, setPosterDragOver] = useState(false);
  const [snack, setSnack] = useState({ open: false, severity: "success", message: "" });

  const [editingCombo, setEditingCombo] = useState(null);
  const [isNew, setIsNew] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, combo: null });

  const [searchText, setSearchText] = useState("");
  const [dragIndex, setDragIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  // ── Camera Config Dialog state ──
  const [comboConfigOpen, setComboConfigOpen] = useState(false);
  const [configCombo, setConfigCombo] = useState(null);

  const [configDevices, setConfigDevices] = useState([]);
  const [configDevicesLoading, setConfigDevicesLoading] = useState(false);
  const [configHasMore, setConfigHasMore] = useState(true);
  const [configSelectedDevice, setConfigSelectedDevice] = useState(null);
  const [configSearchText, setConfigSearchText] = useState("");
  const configSearchTimer = useRef(null);
  const configDeviceListRef = useRef(null);

  const configCanvasRef = useRef(null);
  const [configBoundary, setConfigBoundary] = useState(null);
  const [, setConfigImgSize] = useState({ width: 0, height: 0 });
  const [configImgBroken, setConfigImgBroken] = useState(false);
  const [configDrawing, setConfigDrawing] = useState(false);
  const [configDrawStart, setConfigDrawStart] = useState(null);

  const [comboModels, setComboModels] = useState([]);
  const [comboTrackingEnabled, setComboTrackingEnabled] = useState(false);
  const [comboTracking, setComboTracking] = useState({ ...DEFAULT_TRACKING });
  const [comboMonitoringTimes, setComboMonitoringTimes] = useState([]);

  const [MODEL_GROUPS, setModelGroups] = useState([]);
  const MODEL_OPTIONS = useMemo(() => MODEL_GROUPS.flatMap(g => g.models), [MODEL_GROUPS]);
  const [configActiveClassId, setConfigActiveClassId] = useState(null);
  const [configModelPopoverAnchor, setConfigModelPopoverAnchor] = useState(null);

  const [configSaving, setConfigSaving] = useState(false);

  // Existing combo config data (to know which cameras are configured)
  const [serverComboConfig, setServerComboConfig] = useState(null);
  const [servers, setServers] = useState([]);

  useEffect(() => {
    if (!posterFile) {
      setPosterPreviewUrl("");
      return;
    }
    const objectUrl = URL.createObjectURL(posterFile);
    setPosterPreviewUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [posterFile]);

  /* ── Fetch servers ── */
  useEffect(() => {
    fetch(`${BASE}/servers/servers-info`).then(r => r.json())
      .then(data => setServers(Array.isArray(data) ? data : []))
      .catch(() => setServers([]));
  }, []);

  /* ── Fetch models → build class options + model groups ── */
  useEffect(() => {
    fetch(`${BASE}/api/v1/models`).then(r => r.json()).then(data => {
      if (!Array.isArray(data)) return;
      const classes = [];
      const groups = data.map(m => ({
        header: m.model_name,
        model_id: m.model_id,
        models: (m.model_versions || []).map(v => ({
          key: String(v.model_version_id),
          label: v.version_display_name,
          model_id: m.model_id,
        })),
      }));
      data.forEach(m => {
        (m.model_classes || m.classes || []).forEach(c => {
          const key = c.model_class_id ?? c.class_config_id;
          classes.push({ key, label: c.class_display_name });
        });
      });
      const uniq = Array.from(new Map(classes.map(c => [c.key, c])).values());
      setClassOptions(uniq);
      setModelGroups(groups);
    }).catch(() => setSnack({ open: true, severity: "error", message: "Failed to load models" }));
  }, []);

  const visibleCombos = useMemo(
    () => (isComboRestricted ? combos.filter(combo => allowedComboIds.includes(Number(combo.id))) : combos),
    [combos, isComboRestricted, allowedComboIds]
  );

  /* ── Fetch combos ── */
  const fetchCombos = useCallback(() => {
    setLoading(true);
    fetch(`${BASE}/api/v1/get-combo-configs`).then(r => r.json())
      .then(data => setCombos(Array.isArray(data) ? data.map(normalizeComboFromApi) : []))
      .catch(() => setCombos([]))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { fetchCombos(); }, [fetchCombos]);

  /* ── Item CRUD ── */
  const addItem = () => setEditingCombo(p => ({ ...p, items: [...p.items, { ...EMPTY_ITEM, order_no: p.items.length + 1 }] }));
  const duplicateItem = (i) => setEditingCombo(p => {
    const items = [...p.items]; items.splice(i + 1, 0, { ...items[i], id: 0, order_no: items.length + 1 });
    return { ...p, items: items.map((x, j) => ({ ...x, order_no: j + 1 })) };
  });
  const removeItem = (i) => setEditingCombo(p => ({ ...p, items: p.items.filter((_, j) => j !== i).map((x, j) => ({ ...x, order_no: j + 1 })) }));
  const updateItem = (i, f, v) => setEditingCombo(p => ({
    ...p, items: p.items.map((item, j) => {
      if (j !== i) return item;
      return { ...item, [f]: v };
    })
  }));

  /* ── DnD ── */
  const handleDragStart = (i) => setDragIndex(i);
  const handleDragOver = (e, i) => { e.preventDefault(); setDragOverIndex(i); };
  const handleDragEnd = () => {
    if (dragIndex != null && dragOverIndex != null && dragIndex !== dragOverIndex) {
      setEditingCombo(p => {
        const items = [...p.items]; const [m] = items.splice(dragIndex, 1); items.splice(dragOverIndex, 0, m);
        return { ...p, items: items.map((x, j) => ({ ...x, order_no: j + 1 })) };
      });
    }
    setDragIndex(null); setDragOverIndex(null);
  };

  /* ── Validate / Save / Delete ── */
  const validateCombo = () => {
    if (!editingCombo.combo_name?.trim()) return "Combo name is required";
    if (!editingCombo.display_name?.trim()) return "Display name is required";
    if (!editingCombo.items.length) return "Add at least one class item";
    for (let i = 0; i < editingCombo.items.length; i++) {
      const item = editingCombo.items[i];
      if (!item.class_id) return `Item ${i + 1}: select a detection class`;
      if (item.min_objects < 1) return `Item ${i + 1}: min objects must be at least 1`;
    }
    if (editingCombo.combo_fps < 1) return "Combo FPS must be at least 1";
    if (editingCombo.probability_threshold < 0 || editingCombo.probability_threshold > 1) return "Probability must be 0\u20131";
    return null;
  };

  const handleSave = () => {
    const err = validateCombo();
    if (err) { setSnack({ open: true, severity: "warning", message: err }); return; }
    setSaving(true);
    const url = isNew ? `${BASE}/api/v1/add-combo-config` : `${BASE}/api/v1/update-combo-config`;
    const payload = buildComboPayload(editingCombo, isNew);
    const formData = new FormData();
    formData.append("payload", JSON.stringify(payload));
    if (posterFile) formData.append("poster_image", posterFile);
    fetch(url, { method: isNew ? "POST" : "PUT", body: formData })
      .then(async (r) => {
        const bodyText = await r.text().catch(() => "");
        if (!r.ok) throw new Error(`HTTP ${r.status}${bodyText ? `: ${bodyText}` : ""}`);
        return JSON.parse(bodyText);
      })
      .then(() => {
        setSnack({ open: true, severity: "success", message: isNew ? "Combo created" : "Combo updated" });
        setPosterFile(null);
        setEditingCombo(null); setSelectedId(null); fetchCombos();
      })
      .catch(err => { const msg = err?.message?.includes("HTTP") ? `Server error (${err.message})` : "Network error"; setSnack({ open: true, severity: "error", message: msg }); })
      .finally(() => setSaving(false));
  };

  const handleDelete = () => {
    const combo = deleteDialog.combo; if (!combo) return;
    const payload = buildComboPayload(combo, false);
    payload.status = 0;
    fetch(`${BASE}/api/v1/update-combo-config`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) })
      .then(r => {
        if (!r.ok) throw new Error();
        setCombos(p => p.filter(x => x.id !== combo.id));
        if (selectedId === combo.id) { setEditingCombo(null); setSelectedId(null); }
        setSnack({ open: true, severity: "success", message: "Deleted" });
      })
      .catch(() => setSnack({ open: true, severity: "error", message: "Failed to delete" }))
      .finally(() => setDeleteDialog({ open: false, combo: null }));
  };

  const openNewForm = () => { setPosterFile(null); setEditingCombo({ ...EMPTY_COMBO, items: [{ ...EMPTY_ITEM }] }); setIsNew(true); setSelectedId(null); };
  const openEditForm = (combo) => {
    const clone = JSON.parse(JSON.stringify(normalizeComboFromApi(combo)));
    setPosterFile(null);
    setEditingCombo(clone); setIsNew(false); setSelectedId(combo.id);
  };

  /* ── Name helpers ── */
  const getClassName = (cid) => classOptions.find(c => c.key === cid)?.label || (cid ? `Class ${cid}` : "");
  const isItemComplete = (item) => !!item.class_id;

  const activePosterPreview = posterPreviewUrl || editingCombo?.poster_url || "";

  const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
  const ALLOWED_IMAGE_EXTS = ['.jpg', '.jpeg', '.png', '.webp', '.gif'];

  const setPosterFromFile = (file) => {
    if (!file) return;
    const ext = file.name?.substring(file.name.lastIndexOf('.')).toLowerCase();
    if (!ALLOWED_IMAGE_TYPES.includes(file.type) && !ALLOWED_IMAGE_EXTS.includes(ext)) {
      setSnack({ open: true, severity: "warning", message: "Only JPG, PNG, WebP and GIF images are allowed" });
      return;
    }
    setPosterFile(file);
  };

  const handlePosterFileChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    setPosterFromFile(file);
  };

  const handlePosterDrop = (e) => {
    e.preventDefault();
    setPosterDragOver(false);
    const file = e.dataTransfer?.files?.[0];
    setPosterFromFile(file);
  };

  // ═══════════════════════════════════════════════════════════════
  // CAMERA COMBO CONFIG DIALOG LOGIC
  // ═══════════════════════════════════════════════════════════════

  const fetchConfigDevices = useCallback((params = {}, append = false) => {
    setConfigDevicesLoading(true);
    const query = new URLSearchParams();
    query.set("limit", String(DEVICE_LIMIT));
    if (params.device_name) query.set("device_name", params.device_name);
    if (params.last_device_id) query.set("last_device_id", String(params.last_device_id));
    fetch(`${BASE}/api/v1/devices?${query.toString()}`).then(r => r.json())
      .then(data => {
        const list = Array.isArray(data) ? data : [];
        setConfigDevices(prev => append ? [...prev, ...list] : list);
        setConfigHasMore(list.length >= DEVICE_LIMIT);
      })
      .catch(() => { if (!append) setConfigDevices([]); setConfigHasMore(false); })
      .finally(() => setConfigDevicesLoading(false));
  }, []);

  const handleConfigDeviceScroll = useCallback((e) => {
    const el = e.target;
    if (configDevicesLoading || !configHasMore) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 40) {
      const lastDevice = configDevices[configDevices.length - 1];
      if (!lastDevice) return;
      const params = {};
      if (configSearchText.trim().length >= 3) params.device_name = configSearchText.trim();
      params.last_device_id = lastDevice.uid;
      fetchConfigDevices(params, true);
    }
  }, [configDevices, configDevicesLoading, configHasMore, configSearchText, fetchConfigDevices]);

  // Debounced search for config devices
  useEffect(() => {
    if (!comboConfigOpen) return;
    clearTimeout(configSearchTimer.current);
    const trimmed = configSearchText.trim();
    if (trimmed.length > 0 && trimmed.length < 3) return;
    const params = {};
    if (trimmed.length >= 3) params.device_name = trimmed;
    if (trimmed.length >= 3) {
      configSearchTimer.current = setTimeout(() => fetchConfigDevices(params), 400);
    } else {
      fetchConfigDevices(params);
    }
    return () => clearTimeout(configSearchTimer.current);
  }, [configSearchText, comboConfigOpen, fetchConfigDevices]);

  // Open config dialog
  const openComboConfig = (combo) => {
    setConfigCombo(combo);
    setComboConfigOpen(true);
    setConfigSelectedDevice(null);
    setConfigBoundary(null);
    setComboModels([]);
    setComboTrackingEnabled(false);
    setComboTracking({ ...DEFAULT_TRACKING });
    setComboMonitoringTimes([]);
    setConfigActiveClassId(null);
    setConfigSearchText("");
    setConfigImgSize({ width: 0, height: 0 });
    setConfigImgBroken(false);
    // Fetch server combo config to check which cameras already have config
    if (servers.length > 0) {
      const serverId = servers[0].ServerId;
      fetch(`${BASE}/api/v1/servers/${serverId}/combo-config`)
        .then(r => r.json())
        .then(data => setServerComboConfig(data))
        .catch(() => setServerComboConfig(null));
    }
  };

  const closeComboConfig = () => {
    setComboConfigOpen(false);
    setConfigCombo(null);
    setConfigSelectedDevice(null);
  };

  // When a camera is selected in the config dialog, load its existing config
  const selectConfigDevice = (device) => {
    setConfigSelectedDevice(device);
    setConfigBoundary(null);
    setConfigImgBroken(false);
    setConfigImgSize({ width: 0, height: 0 });
    setConfigActiveClassId(null);

    // Initialize model configs from combo items (classes)
    const items = configCombo?.items || [];
    const defaultModels = items.filter(it => it.class_id).map(it => ({
      ...DEFAULT_MODEL_CFG,
      class_id: it.class_id,
    }));
    setComboModels(defaultModels);
    setComboTrackingEnabled(false);
    setComboTracking({ ...DEFAULT_TRACKING });
    setComboMonitoringTimes([]);

    if (configCombo && items.length > 0) {
      setConfigActiveClassId(items[0].class_id);
    }

    // Check if this camera+combo already has config
    if (serverComboConfig?.cameras) {
      const camConfig = serverComboConfig.cameras[String(device.deviceid)];
      if (camConfig?.combos) {
        const combo = camConfig.combos[configCombo?.combo_name];
        if (combo) {
          // Load boundary
          setConfigBoundary(combo.combo_boundary || null);

          // Load models
          if (combo.models && combo.classes) {
            const loadedModels = [];
            Object.entries(combo.classes).forEach(([className, classInfo]) => {
              const modelInfo = combo.models[classInfo.model_name];
              if (modelInfo) {
                // Find the class_id from our items
                const matchItem = items.find(it => {
                  const cn = classOptions.find(c => c.key === it.class_id)?.label;
                  return cn === className;
                });
                const classId = matchItem?.class_id || classInfo.class_id;
                // Find model_version_id from MODEL_OPTIONS
                const mv = MODEL_OPTIONS.find(o => o.label === modelInfo.version_display_name);
                loadedModels.push({
                  class_id: classId,
                  model_config_id: modelInfo.model_config_id || 0,
                  model_version_id: mv ? Number(mv.key) : 0,
                  model_version_label: modelInfo.version_display_name || "",
                  conf: modelInfo.conf,
                  iou: modelInfo.iou,
                  detect_fps: modelInfo.detect_fps,
                  model_format: "pt",
                  agnostic_nms: false,
                  is_zoom_motion_area: false,
                  communication_type: modelInfo.communication?.type || "grpc",
                  grpc_connect_ip: modelInfo.communication?.grpc?.ip || "127.0.0.1",
                  grpc_connect_port: modelInfo.communication?.grpc?.port || 50051,
                  http_private_base_url: modelInfo.communication?.http?.private_base_url || "",
                  http_public_base_url: modelInfo.communication?.http?.public_base_url || "",
                  http_path: modelInfo.communication?.http?.path || "",
                });
              }
            });
            if (loadedModels.length > 0) {
              // Merge with defaults for classes not yet configured
              const finalModels = items.filter(it => it.class_id).map(it => {
                const existing = loadedModels.find(m => m.class_id === it.class_id);
                return existing || { ...DEFAULT_MODEL_CFG, class_id: it.class_id };
              });
              setComboModels(finalModels);
            }
          }

          // Load tracking
          if (combo.tracking?.enabled && combo.tracking.config) {
            setComboTrackingEnabled(true);
            setComboTracking(prev => ({ ...prev, ...combo.tracking.config }));
          } else {
            setComboTrackingEnabled(false);
            setComboTracking({ ...DEFAULT_TRACKING });
          }

          // Load monitoring times
          if (Array.isArray(combo.combo_monitoring_times) && combo.combo_monitoring_times.length > 0) {
            // Group by start_time+end_time
            const rangeMap = {};
            combo.combo_monitoring_times.forEach(mt => {
              const key = `${mt.start_time}_${mt.end_time}`;
              if (!rangeMap[key]) {
                rangeMap[key] = { startTime: mt.start_time, endTime: mt.end_time, days: [], dayIds: {} };
              }
              const dayName = NUM_TO_DAY_NAME[mt.day_of_week] || "Mon";
              if (!rangeMap[key].days.includes(dayName)) {
                rangeMap[key].days.push(dayName);
              }
              rangeMap[key].dayIds[dayName] = mt.id;
            });
            setComboMonitoringTimes(Object.values(rangeMap));
          } else {
            setComboMonitoringTimes([]);
          }
        }
      }
    }
  };

  // Canvas drawing for combo boundary rectangle
  const redrawConfigCanvas = useCallback(() => {
    const canvas = configCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    if (configBoundary) {
      const b = configBoundary;
      ctx.strokeStyle = UI.green;
      ctx.lineWidth = 2;
      ctx.setLineDash([6, 3]);
      ctx.strokeRect(
        b.x * canvas.width,
        b.y * canvas.height,
        b.w * canvas.width,
        b.h * canvas.height
      );
      ctx.setLineDash([]);
      ctx.fillStyle = alpha(theme.palette.success.main, 0.08);
      ctx.fillRect(
        b.x * canvas.width,
        b.y * canvas.height,
        b.w * canvas.width,
        b.h * canvas.height
      );
    }
  }, [configBoundary, theme, UI.green]);

  useEffect(() => { redrawConfigCanvas(); }, [redrawConfigCanvas]);

  const getConfigMousePos = (e) => {
    const canvas = configCanvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    return {
      x: (e.clientX - rect.left) / rect.width,
      y: (e.clientY - rect.top) / rect.height,
    };
  };

  const handleConfigCanvasMouseDown = (e) => {
    const pos = getConfigMousePos(e);
    setConfigDrawing(true);
    setConfigDrawStart(pos);
  };

  const handleConfigCanvasMouseMove = (e) => {
    if (!configDrawing || !configDrawStart) return;
    const pos = getConfigMousePos(e);
    const canvas = configCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Draw existing boundary faded
    if (configBoundary) {
      ctx.strokeStyle = alpha(theme.palette.success.main, 0.3);
      ctx.lineWidth = 1;
      ctx.setLineDash([4, 4]);
      ctx.strokeRect(
        configBoundary.x * canvas.width,
        configBoundary.y * canvas.height,
        configBoundary.w * canvas.width,
        configBoundary.h * canvas.height
      );
      ctx.setLineDash([]);
    }

    // Draw preview
    const x = Math.min(configDrawStart.x, pos.x);
    const y = Math.min(configDrawStart.y, pos.y);
    const w = Math.abs(pos.x - configDrawStart.x);
    const h = Math.abs(pos.y - configDrawStart.y);
    ctx.strokeStyle = UI.green;
    ctx.lineWidth = 2;
    ctx.setLineDash([6, 3]);
    ctx.strokeRect(x * canvas.width, y * canvas.height, w * canvas.width, h * canvas.height);
    ctx.setLineDash([]);
    ctx.fillStyle = alpha(theme.palette.success.main, 0.1);
    ctx.fillRect(x * canvas.width, y * canvas.height, w * canvas.width, h * canvas.height);
  };

  const handleConfigCanvasMouseUp = (e) => {
    if (!configDrawing || !configDrawStart) {
      setConfigDrawing(false);
      return;
    }
    const pos = getConfigMousePos(e);
    const x = Math.min(configDrawStart.x, pos.x);
    const y = Math.min(configDrawStart.y, pos.y);
    const w = Math.abs(pos.x - configDrawStart.x);
    const h = Math.abs(pos.y - configDrawStart.y);
    if (w > 0.01 && h > 0.01) {
      setConfigBoundary({ x, y, w, h });
    }
    setConfigDrawing(false);
    setConfigDrawStart(null);
  };

  // Model config per class
  const activeModelCfg = useMemo(() => {
    return comboModels.find(m => m.class_id === configActiveClassId) || null;
  }, [comboModels, configActiveClassId]);

  const updateActiveModelField = (field, value) => {
    setComboModels(prev => prev.map(m =>
      m.class_id === configActiveClassId ? { ...m, [field]: value } : m
    ));
  };

  const selectModelVersion = (modelVersionKey) => {
    const mv = MODEL_OPTIONS.find(o => o.key === modelVersionKey);
    setComboModels(prev => prev.map(m =>
      m.class_id === configActiveClassId
        ? { ...m, model_version_id: Number(modelVersionKey), model_version_label: mv?.label || "" }
        : m
    ));
    setConfigModelPopoverAnchor(null);
  };

  // Tracking helpers
  const updateTracking = (field, value) => {
    setComboTracking(prev => ({ ...prev, [field]: value }));
  };

  // Monitoring time helpers
  const addMonitoringTimeRange = () => {
    setComboMonitoringTimes(prev => [...prev, { startTime: "08:00", endTime: "18:00", days: ["Mon", "Tue", "Wed", "Thu", "Fri"], dayIds: {} }]);
  };
  const updateMonitoringTimeRange = (idx, field, value) => {
    setComboMonitoringTimes(prev => prev.map((r, i) => i === idx ? { ...r, [field]: value } : r));
  };
  const toggleMonitoringDay = (idx, day) => {
    setComboMonitoringTimes(prev => prev.map((r, i) => {
      if (i !== idx) return r;
      const days = r.days.includes(day) ? r.days.filter(d => d !== day) : [...r.days, day];
      return { ...r, days };
    }));
  };
  const removeMonitoringTimeRange = (idx) => {
    setComboMonitoringTimes(prev => prev.filter((_, i) => i !== idx));
  };

  // Check if a camera has config for the current configCombo
  const cameraHasComboConfig = useCallback((deviceId) => {
    if (!serverComboConfig?.cameras || !configCombo) return false;
    const cam = serverComboConfig.cameras[String(deviceId)];
    return cam?.combos?.[configCombo.combo_name] != null;
  }, [serverComboConfig, configCombo]);

  // Save camera combo config
  const handleComboConfigSave = () => {
    if (!configSelectedDevice?.deviceid || !configCombo?.id) {
      setSnack({ open: true, severity: "warning", message: "Select a camera first" });
      return;
    }
    // Validate that all classes have models assigned
    for (const m of comboModels) {
      if (!m.model_version_id) {
        const cn = getClassName(m.class_id);
        setSnack({ open: true, severity: "warning", message: `Assign a model to class "${cn}"` });
        return;
      }
    }

    setConfigSaving(true);
    const monitoringTimesPayload = [];
    comboMonitoringTimes.forEach(range => {
      range.days.forEach(day => {
        monitoringTimesPayload.push({
          id: (range.dayIds && range.dayIds[day]) || 0,
          day_of_week: DAY_NAME_TO_NUM[day] ?? 1,
          start_time: range.startTime,
          end_time: range.endTime,
        });
      });
    });

    const payload = {
      camera_id: configSelectedDevice.deviceid,
      combo_rule_id: configCombo.id,
      combo_boundary: configBoundary,
      models: comboModels.map(m => ({
        class_id: m.class_id,
        model_config_id: m.model_config_id || 0,
        model_version_id: m.model_version_id,
        conf: m.conf,
        iou: m.iou,
        detect_fps: m.detect_fps,
        model_format: m.model_format || "pt",
        agnostic_nms: m.agnostic_nms || false,
        is_zoom_motion_area: m.is_zoom_motion_area || false,
        communication_type: m.communication_type || "grpc",
        grpc_connect_ip: m.grpc_connect_ip || null,
        grpc_connect_port: m.grpc_connect_port || null,
        http_private_base_url: m.http_private_base_url || null,
        http_public_base_url: m.http_public_base_url || null,
        http_path: m.http_path || null,
      })),
      tracking: comboTrackingEnabled ? comboTracking : null,
      monitoring_times: monitoringTimesPayload.length > 0 ? monitoringTimesPayload : null,
    };

    fetch(`${BASE}/api/v1/update-camera-combo-config`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    })
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then(() => {
        setSnack({ open: true, severity: "success", message: "Camera combo config saved" });
        // Refresh server combo config
        if (servers.length > 0) {
          const serverId = servers[0].ServerId;
          fetch(`${BASE}/api/v1/servers/${serverId}/combo-config`)
            .then(r => r.json())
            .then(data => setServerComboConfig(data))
            .catch(() => {});
        }
      })
      .catch(err => {
        setSnack({ open: true, severity: "error", message: `Save failed: ${err.message}` });
      })
      .finally(() => setConfigSaving(false));
  };

  /* ═══════════════ RENDER ═══════════════ */
  return (
    <Page title="Combo Models | VDOIntel">
      <Container
        maxWidth="xxl"
        sx={{
          height: 'calc(100vh - 100px)',
          scrollbarWidth: 'thin',
          scrollbarColor: `${UI.scrollThumb} transparent`,
          '& *::-webkit-scrollbar': { width: 8, height: 8 },
          '& *::-webkit-scrollbar-track': { background: 'transparent' },
          '& *::-webkit-scrollbar-thumb': { backgroundColor: UI.scrollThumb, borderRadius: 4 },
          '& *::-webkit-scrollbar-thumb:hover': { backgroundColor: UI.scrollThumbHover },
        }}
      >
        <Box sx={{ mb: 3 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
            <Typography variant="h4">{isComboRestricted ? 'Combination Models' : 'Combo Model Config'}</Typography>
          </Box>
          <Breadcrumbs separator="•" aria-label="breadcrumb">
            <DashboardBreadcrumbLink sx={{ fontSize: '0.875rem' }} />
            {!isComboRestricted && <Typography color="text.secondary" sx={{ fontSize: '0.875rem' }}>Settings</Typography>}
            <Typography color="text.primary" sx={{ fontSize: '0.875rem' }}>
              {isComboRestricted ? 'Combination Models' : 'Combo Model Config'}
            </Typography>
          </Breadcrumbs>
        </Box>

        <Grid container spacing={2} sx={{ height: 'calc(100% - 50px)' }}>

          {/* ── LEFT: List Panel ── */}
          <Grid item xs={12} md={4} lg={3} sx={{ height: '100%' }}>
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Stack sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${theme.palette.divider}` }} spacing={1}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="subtitle2">
                    All Combos {!loading && <Typography component="span" variant="caption" color="text.secondary">({visibleCombos.length})</Typography>}
                  </Typography>
                  {!isComboRestricted && <Button size="small" startIcon={<AddIcon />} onClick={openNewForm} sx={{ textTransform: 'none', fontSize: 12 }}>New</Button>}
                </Stack>
                <TextField
                  size="small" fullWidth placeholder="Search combos..."
                  value={searchText} onChange={e => setSearchText(e.target.value)}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: 'text.disabled' }} /></InputAdornment>,
                    sx: { borderRadius: 2, bgcolor: searchInputBg, '& fieldset': { borderColor: searchInputBorder } },
                  }}
                />
              </Stack>
              <Box sx={{ flex: 1, overflowY: 'auto', p: 1 }}>
                {loading ? (
                  <Stack spacing={1} sx={{ p: 1 }}>{[1,2,3].map(i => <Skeleton key={i} variant="rectangular" height={52} sx={{ borderRadius: 1 }} />)}</Stack>
                ) : (() => {
                  const q = searchText.trim().toLowerCase();
                  const filtered = q ? visibleCombos.filter(c => (c.combo_name || '').toLowerCase().includes(q) || (c.display_name || '').toLowerCase().includes(q) || (c.description || '').toLowerCase().includes(q)) : visibleCombos;
                  return filtered.length === 0 ? (
                  <Stack alignItems="center" justifyContent="center" sx={{ height: '100%', py: 6 }}>
                    <Box sx={{ width: 44, height: 44, borderRadius: '50%', bgcolor: alpha(theme.palette.primary.main, 0.1), display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1.5 }}>
                      <AddIcon sx={{ color: theme.palette.primary.main }} />
                    </Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>{q ? "No matches" : "No combos yet"}</Typography>
                    {!q && !isComboRestricted && <Button size="small" onClick={openNewForm}>Create first</Button>}
                  </Stack>
                ) : filtered.map((combo) => {
                  const active = selectedId === combo.id;
                  return (
                    <Box key={combo.id} onClick={() => openEditForm(combo)} sx={{
                      px: 1.5, py: 1.25, mb: 0.5, borderRadius: 1.5, cursor: 'pointer',
                      bgcolor: active ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
                      border: `1px solid ${active ? theme.palette.primary.main : 'transparent'}`,
                      transition: 'all 0.15s',
                      '&:hover': { bgcolor: active ? alpha(theme.palette.primary.main, 0.1) : theme.palette.action.hover },
                    }}>
                      <Stack direction="row" alignItems="center" justifyContent="space-between">
                        <Stack direction="row" spacing={1.25} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
                          <Box
                            sx={{
                              width: 46,
                              height: 46,
                              borderRadius: 1,
                              overflow: 'hidden',
                              bgcolor: listThumbBg,
                              flexShrink: 0,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            {combo.poster_url ? (
                              <Box component="img" src={combo.poster_url} alt={combo.display_name || combo.combo_name || "poster"}
                                sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            ) : (
                              <CloudUploadIcon sx={{ fontSize: 18, color: theme.palette.text.disabled }} />
                            )}
                          </Box>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="subtitle2" noWrap>
                              {combo.display_name || combo.combo_name || "Untitled"}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                              }}
                            >
                              {combo.description || "No description"}
                            </Typography>
                          </Box>
                        </Stack>
                        <Stack direction="row" spacing={0} onClick={e => e.stopPropagation()}>
                          <IconButton size="small" onClick={() => setDeleteDialog({ open: true, combo })}
                            sx={{ p: 0.25, '&:hover': { color: theme.palette.error.main } }}>
                            <DeleteIcon sx={{ fontSize: 15 }} />
                          </IconButton>
                        </Stack>
                      </Stack>
                    </Box>
                  );
                }); })()}
              </Box>
            </Card>
          </Grid>

          {/* ── RIGHT: Detail/Form Panel ── */}
          <Grid item xs={12} md={8} lg={9} sx={{ height: '100%' }}>
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              {!editingCombo ? (
                <Stack alignItems="center" justifyContent="center" sx={{ flex: 1 }}>
                  <Box sx={{ width: 56, height: 56, borderRadius: '50%', bgcolor: emptyStateBg, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
                    <EditIcon sx={{ color: theme.palette.text.disabled, fontSize: 24 }} />
                  </Box>
                  <Typography variant="subtitle1" color="text.secondary">Select a combo to edit</Typography>
                  {!isComboRestricted && <Typography variant="caption" color="text.disabled">or create a new one</Typography>}
                  {!isComboRestricted && <Button size="small" startIcon={<AddIcon />} onClick={openNewForm} sx={{ mt: 2 }}>New Combination</Button>}
                </Stack>
              ) : (
                <>
                  {/* Form header */}
                  <Stack direction="row" justifyContent="space-between" alignItems="center"
                    sx={{ px: 2.5, py: 1.5, borderBottom: `1px solid ${theme.palette.divider}`, flexShrink: 0 }}>
                    <Stack direction="row" alignItems="center" spacing={1.5}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        {isNew ? "New Combination" : editingCombo.combo_name || "Untitled"}
                      </Typography>
                      {!isNew && <Chip label="Editing" size="small" color="primary" variant="outlined" sx={{ height: 20, fontSize: 10 }} />}
                    </Stack>
                    <Stack direction="row" spacing={1}>
                      <Button size="small" variant="outlined" onClick={() => { setEditingCombo(null); setSelectedId(null); }}>Cancel</Button>
                      <Button size="small" variant="contained" disabled={saving} onClick={handleSave}
                        startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon sx={{ fontSize: 16 }} />}>
                        {saving ? "Saving..." : "Save"}
                      </Button>
                    </Stack>
                  </Stack>

                  {/* Form body */}
                  <Box sx={{ flex: 1, overflowY: 'auto', p: 2.5 }}>

                    {/* Names */}
                    <Box
                      sx={{
                        mb: 2.5,
                        p: 2,
                        borderRadius: 2,
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.22)}`,
                        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.06)} 0%, ${alpha(theme.palette.info.main, 0.04)} 100%)`,
                      }}
                    >
                      <Grid container spacing={2.5}>
                        <Grid item xs={12} md={4}>
                          <Box
                            onClick={() => posterInputRef.current?.click()}
                            onDragOver={(e) => { e.preventDefault(); setPosterDragOver(true); }}
                            onDragLeave={() => setPosterDragOver(false)}
                            onDrop={handlePosterDrop}
                            sx={{
                              position: 'relative',
                              minHeight: 220,
                              borderRadius: 2,
                              overflow: 'hidden',
                              cursor: 'pointer',
                              border: `2px dashed ${posterDragOver ? theme.palette.primary.main : alpha(theme.palette.text.secondary, 0.35)}`,
                              bgcolor: posterDragOver ? alpha(theme.palette.primary.main, 0.08) : alpha(theme.palette.background.default, 0.6),
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              transition: 'all 0.2s ease',
                            }}
                          >
                            {activePosterPreview ? (
                              <Box
                                component="img"
                                src={activePosterPreview}
                                alt="Poster preview"
                                sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                              />
                            ) : (
                              <Stack alignItems="center" spacing={1}>
                                <CloudUploadIcon sx={{ fontSize: 28, color: theme.palette.primary.main }} />
                                <Typography variant="subtitle2">Drop poster here</Typography>
                                <Typography variant="caption" color="text.secondary">or click to browse</Typography>
                              </Stack>
                            )}
                            {activePosterPreview && (
                              <>
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setPosterFile(null);
                                    setEditingCombo(p => ({ ...p, poster_url: "" }));
                                  }}
                                  sx={{
                                    position: 'absolute',
                                    top: 8,
                                    right: 8,
                                    bgcolor: alpha(theme.palette.common.black, 0.55),
                                    color: 'common.white',
                                    '&:hover': { bgcolor: alpha(theme.palette.common.black, 0.72) },
                                  }}
                                >
                                  <CloseIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                                <Box
                                  sx={{
                                    position: 'absolute',
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    p: 1,
                                    background: `linear-gradient(180deg, ${alpha(theme.palette.common.black, 0)} 0%, ${alpha(theme.palette.common.black, 0.55)} 100%)`,
                                  }}
                                >
                                  <Typography variant="caption" sx={{ color: 'common.white' }}>
                                    {posterFile?.name || "Current poster"}
                                  </Typography>
                                </Box>
                              </>
                            )}
                          </Box>
                          <input
                            ref={posterInputRef}
                            type="file"
                            accept=".jpg,.jpeg,.png,.webp,.gif"
                            hidden
                            onChange={handlePosterFileChange}
                          />
                        </Grid>
                        <Grid item xs={12} md={8}>
                          <Stack spacing={2} sx={{ minHeight: 220, height: '100%' }}>
                            <TextField fullWidth label="Combo Name" size="small"
                              value={editingCombo.combo_name}
                              onChange={e => setEditingCombo(p => ({ ...p, combo_name: e.target.value }))} />
                            <TextField fullWidth label="Display Name" size="small"
                              value={editingCombo.display_name}
                              onChange={e => setEditingCombo(p => ({ ...p, display_name: e.target.value }))} />
                            <TextField
                              fullWidth
                              label="Description"
                              size="small"
                              multiline
                              minRows={4}
                              value={editingCombo.description || ""}
                              onChange={e => setEditingCombo(p => ({ ...p, description: e.target.value }))}
                            />
                          </Stack>
                        </Grid>
                      </Grid>
                    </Box>

                    {/* Settings */}
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                      <TuneIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />
                      <Typography variant="subtitle2">Advanced Settings</Typography>
                    </Stack>
                    <Box sx={{ mb: 2.5, p: 2, border: `1px solid ${theme.palette.divider}`, borderRadius: 1.5, bgcolor: sectionBg }}>
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={6} sm={3}>
                          <TextField fullWidth label="Combo FPS" size="small" type="number"
                            value={editingCombo.combo_fps}
                            onChange={e => setEditingCombo(p => ({ ...p, combo_fps: Math.max(1, Number(e.target.value) || 1) }))}
                            inputProps={{ min: 1 }} />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <TextField fullWidth label="Motion FPS" size="small" type="number"
                            value={editingCombo.combo_motion_fps}
                            onChange={e => setEditingCombo(p => ({ ...p, combo_motion_fps: Math.max(1, Number(e.target.value) || 1) }))}
                            inputProps={{ min: 1 }} />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <TextField fullWidth label="Min Wait Time" size="small" type="number"
                            value={editingCombo.min_wait_time}
                            onChange={e => setEditingCombo(p => ({ ...p, min_wait_time: Math.max(0, Number(e.target.value) || 0) }))}
                            inputProps={{ min: 0 }} />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <Stack spacing={0.5}>
                            <Typography variant="caption" color="text.secondary">Probability: {editingCombo.probability_threshold}</Typography>
                            <Slider size="small" value={editingCombo.probability_threshold} min={0} max={1} step={0.05}
                              onChange={(_, v) => setEditingCombo(p => ({ ...p, probability_threshold: v }))} />
                          </Stack>
                        </Grid>
                      </Grid>
                    </Box>

                    {/* Items */}
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography variant="subtitle2">Detection Items</Typography>
                        <Chip label={editingCombo.items.length} size="small"
                          sx={{ height: 20, bgcolor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main, fontWeight: 700 }} />
                      </Stack>
                      <Button size="small" startIcon={<AddIcon />} onClick={addItem} sx={{ textTransform: 'none' }}>Add</Button>
                    </Stack>

                    {editingCombo.items.map((item, idx) => {
                      const isDragging = dragIndex === idx;
                      const isDragOver = dragOverIndex === idx && dragIndex !== idx;
                      const complete = isItemComplete(item);
                      return (
                        <Box key={idx}>
                          <Box draggable
                            onDragStart={() => handleDragStart(idx)}
                            onDragOver={(e) => handleDragOver(e, idx)}
                            onDragEnd={handleDragEnd}
                            sx={{
                              border: `1px solid ${isDragOver ? theme.palette.primary.main : softBorder}`,
                              borderRadius: 1.5, mb: 1, overflow: 'hidden',
                              opacity: isDragging ? 0.4 : 1, transition: 'all 0.15s',
                              transform: isDragOver ? 'translateY(-2px)' : 'none',
                            }}>
                            {/* Item header */}
                            <Stack direction="row" alignItems="center" justifyContent="space-between"
                              sx={{
                                px: 1.5,
                                py: 0.5,
                                bgcolor: item.is_positive ? alpha(theme.palette.success.main, 0.1) : alpha(theme.palette.error.main, 0.1),
                                borderBottom: `1px solid ${softDivider}`,
                              }}>
                              <Stack direction="row" alignItems="center" spacing={0.5}>
                                <Box sx={{ cursor: 'grab', display: 'flex', color: theme.palette.text.disabled, '&:active': { cursor: 'grabbing' } }}>
                                  <DragIndicatorIcon fontSize="small" />
                                </Box>
                                <Typography variant="subtitle2">#{idx + 1}</Typography>
                                {complete && <Chip label={getClassName(item.class_id)} size="small"
                                  sx={{ height: 20, fontSize: 11, bgcolor: alpha(theme.palette.primary.main, 0.08), color: theme.palette.primary.main }} />}
                              </Stack>
                              <Stack direction="row" spacing={0}>
                                <Tooltip title="Duplicate"><IconButton size="small" onClick={() => duplicateItem(idx)}><ContentCopyIcon sx={{ fontSize: 15 }} /></IconButton></Tooltip>
                                {editingCombo.items.length > 1 && (
                                  <Tooltip title="Remove"><IconButton size="small" onClick={() => removeItem(idx)} sx={{ '&:hover': { color: theme.palette.error.main } }}><CloseIcon sx={{ fontSize: 15 }} /></IconButton></Tooltip>
                                )}
                              </Stack>
                            </Stack>

                            {/* Item fields */}
                            <Grid container spacing={1.5} sx={{ p: 1.5 }} alignItems="center">
                              <Grid item xs={12} md={6}>
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>Class</Typography>
                                <Autocomplete
                                  size="small"
                                  options={classOptions}
                                  value={classOptions.find(c => String(c.key) === String(item.class_id)) || null}
                                  onChange={(_, option) => updateItem(idx, "class_id", option ? option.key : null)}
                                  getOptionLabel={(option) => option?.label || ""}
                                  isOptionEqualToValue={(option, value) => String(option.key) === String(value?.key)}
                                  openOnFocus
                                  autoHighlight
                                  clearOnEscape
                                  noOptionsText="No class found"
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      label="Select Class"
                                      placeholder="Type or select"
                                      sx={{ mt: 0.5 }}
                                    />
                                  )}
                                />
                              </Grid>
                              <Grid item xs={12} md={3}>
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>Exists</Typography>
                                <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                                  <Button
                                    size="small"
                                    variant={item.is_positive ? "contained" : "outlined"}
                                    color={item.is_positive ? "success" : "inherit"}
                                    onClick={() => updateItem(idx, "is_positive", true)}
                                    sx={{ minWidth: 64, textTransform: 'none' }}
                                  >
                                    Yes
                                  </Button>
                                  <Button
                                    size="small"
                                    variant={!item.is_positive ? "contained" : "outlined"}
                                    color={!item.is_positive ? "error" : "inherit"}
                                    onClick={() => updateItem(idx, "is_positive", false)}
                                    sx={{ minWidth: 64, textTransform: 'none' }}
                                  >
                                    No
                                  </Button>
                                </Stack>
                              </Grid>
                              <Grid item xs={12}>
                                <Box sx={{ height: 2 }} />
                              </Grid>
                              <Grid item xs={12} md={3}>
                                <TextField fullWidth label="Min Obj" size="small" type="number"
                                  value={item.min_objects}
                                  onChange={e => updateItem(idx, "min_objects", Math.max(1, Number(e.target.value) || 1))}
                                  inputProps={{ min: 1 }} />
                              </Grid>
                              <Grid item xs={12} md={5}>
                                <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mt: { xs: 0, md: 1 } }}>
                                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, minWidth: 104 }}>
                                    Distance: {item.max_distance}
                                  </Typography>
                                  <Slider
                                    size="small"
                                    value={item.max_distance}
                                    min={0}
                                    max={1}
                                    step={0.05}
                                    onChange={(_, v) => updateItem(idx, "max_distance", v)}
                                    sx={{ width: '100%', maxWidth: 220 }}
                                  />
                                </Stack>
                              </Grid>
                            </Grid>
                          </Box>

                          {/* Condition pill */}
                          {idx < editingCombo.items.length - 1 && (
                            <Stack direction="row" alignItems="center" justifyContent="center" sx={{ py: 0.25, mb: 1 }}>
                              <Divider sx={{ flex: 1 }} />
                              <Stack direction="row" spacing={0.5} sx={{ mx: 1.5, border: `1px solid ${softBorder}`, borderRadius: 3, p: 0.25 }}>
                                {["and", "or"].map(cond => (
                                  <Chip key={cond} label={cond.toUpperCase()} size="small" clickable
                                    onClick={() => updateItem(idx, "condition_type", cond)}
                                    sx={{
                                      fontWeight: 700, fontSize: 11, minWidth: 40,
                                      bgcolor: item.condition_type === cond ? theme.palette.primary.main : 'transparent',
                                      color: item.condition_type === cond ? 'common.white' : theme.palette.text.secondary,
                                      '&:hover': { bgcolor: item.condition_type === cond ? theme.palette.primary.dark : alpha(theme.palette.primary.main, 0.08) },
                                    }} />
                                ))}
                              </Stack>
                              <Divider sx={{ flex: 1 }} />
                            </Stack>
                          )}
                        </Box>
                      );
                    })}

                    <Box onClick={addItem} sx={{
                      mt: 1, py: 1.5, borderRadius: 1.5, cursor: 'pointer', textAlign: 'center',
                      border: `2px dashed ${softBorder}`,
                      '&:hover': { borderColor: theme.palette.primary.main, bgcolor: alpha(theme.palette.primary.main, 0.04) },
                    }}>
                      <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
                        <AddIcon sx={{ fontSize: 18, color: theme.palette.text.secondary }} />
                        <Typography variant="body2" color="text.secondary">Add item</Typography>
                      </Stack>
                    </Box>
                  </Box>
                </>
              )}
            </Card>
          </Grid>
        </Grid>
      </Container>

      {/* ── Delete Dialog ── */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, combo: null })} maxWidth="xs" fullWidth>
        <DialogTitle>Delete "{deleteDialog.combo?.combo_name}"?</DialogTitle>
        <DialogContent><Typography variant="body2" color="text.secondary">This action cannot be undone.</Typography></DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, combo: null })}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>

      {/* ═══════════════════════════════════════════════════════════════
          CAMERA COMBO CONFIG FULL-SCREEN DIALOG
          ═══════════════════════════════════════════════════════════════ */}
      <Dialog
        fullScreen
        open={comboConfigOpen}
        onClose={closeComboConfig}
        TransitionComponent={Transition}
      >
        <Box
          sx={{
            height: "100vh",
            display: "flex",
            flexDirection: "column",
            bgcolor: UI.bg,
            "& .MuiTypography-caption": { fontSize: 11 },
            "& .MuiTypography-body2": { fontSize: 13 },
            "& .MuiTypography-subtitle2": { fontSize: 13 },
            "& .MuiTypography-subtitle1": { fontSize: 14 },
            "& .rp": {
              "& .MuiAccordionSummary-root": { px: 2, minHeight: 42, backgroundColor: UI.bg },
              "& .MuiAccordionSummary-content": { my: 0.75 },
              "& .MuiAccordionSummary-expandIconWrapper": { color: UI.textMuted },
              "& .MuiAccordionDetails-root": { px: 2, pt: 1.5, pb: 2 },
              "& .MuiTextField-root .MuiOutlinedInput-root": { height: 26, borderRadius: 1, backgroundColor: UI.inputBg, fontSize: 11 },
              "& .MuiTextField-root .MuiOutlinedInput-input": { fontSize: 11, padding: "4px 8px" },
              "& .MuiTextField-root .MuiOutlinedInput-notchedOutline": { borderColor: alpha(theme.palette.text.primary, 0.16) },
              "& .MuiTextField-root .MuiInputAdornment-root svg": { fontSize: 12 },
              "& .MuiTextField-root .MuiInputLabel-root": { fontSize: 11 },
              "& .MuiButton-root": { height: 26, borderRadius: 1, fontSize: 11 },
              "& .MuiFormControl-root .MuiOutlinedInput-root": { height: 26, borderRadius: 1, backgroundColor: UI.inputBg, fontSize: 11 },
              "& .MuiSlider-root": { mt: 0.5, mb: 0 },
            },
            "& .MuiSwitch-switchBase.Mui-checked": { color: UI.dark },
            "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: UI.dark },
            "& .MuiAccordion-root": {
              borderRadius: 0, border: "none", boxShadow: "none", backgroundColor: "transparent",
              "&:before": { display: "none" }, mb: 0,
            },
            "& .MuiAccordionSummary-root": {
              minHeight: 36, px: 2, borderBottom: `1px solid ${UI.border}`, background: "transparent",
              "& .MuiAccordionSummary-content": { my: 0.5 },
            },
            "& .MuiAccordionDetails-root": { px: 2, pb: 1.5 },
          }}
        >
          <Grid container spacing={0} columns={16} sx={{ flex: 1, overflow: "hidden" }}>

            {/* ── LEFT: Camera List ── */}
            <Grid item xs={4} md={3} lg={2}>
              <Paper elevation={0} sx={{ height: "100%", display: "flex", flexDirection: "column", borderRadius: 0, backgroundColor: UI.panel, borderRight: `1px solid ${UI.border}` }}>
                <Box sx={{ position: "sticky", top: 0, zIndex: 10, borderBottom: `1px solid ${UI.border}`, backgroundColor: UI.panel }}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" px={2} pt={1.5} pb={1}>
                    <Typography variant="subtitle2" fontWeight={600}>
                      Cameras
                      <Typography component="span" variant="caption" color="text.secondary" ml={0.5}>
                        ({configDevices.length}{configHasMore ? "+" : ""})
                      </Typography>
                    </Typography>
                  </Stack>
                  <Box sx={{ px: 1.5, pb: 1.5 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, backgroundColor: UI.inputBg, borderRadius: 2, px: 1.25, height: 32 }}>
                      <SearchIcon sx={{ fontSize: 15, color: UI.textMuted, flexShrink: 0 }} />
                      <input
                        placeholder="Search cameras (min 3 chars)"
                        value={configSearchText}
                        onChange={(e) => setConfigSearchText(e.target.value)}
                        style={{ border: "none", outline: "none", background: "transparent", fontSize: 12, color: UI.textMain, width: "100%", padding: 0 }}
                      />
                    </Box>
                  </Box>
                </Box>

                <Box
                  ref={configDeviceListRef}
                  onScroll={handleConfigDeviceScroll}
                  sx={{
                    flex: 1, overflowY: "auto", overflowX: "hidden", px: 1.5, pt: 1, pb: 1.5,
                    "&::-webkit-scrollbar": { width: 4 },
                    "&::-webkit-scrollbar-track": { background: "transparent" },
                    "&::-webkit-scrollbar-thumb": { background: UI.border, borderRadius: 2 },
                  }}
                >
                  <Stack spacing={1.5}>
                    {configDevices.map((item) => {
                      const hasConfig = cameraHasComboConfig(item.deviceid);
                      return (
                        <Box
                          key={item.uid}
                          onClick={() => selectConfigDevice(item)}
                          sx={{
                            cursor: "pointer", borderRadius: 2, overflow: "hidden", position: "relative",
                            backgroundColor: configSelectedDevice?.uid === item.uid ? UI.accentSoft : "transparent",
                            border: "none", transition: "all 0.15s",
                          }}
                        >
                          {hasConfig && (
                            <Box sx={{ position: "absolute", top: 6, right: 6, width: 8, height: 8, borderRadius: "50%", bgcolor: UI.green, zIndex: 1 }} />
                          )}
                          <Box sx={{ width: "100%", aspectRatio: "16/10", overflow: "hidden", borderRadius: 1.5, backgroundColor: UI.inputBg, display: "flex", alignItems: "center", justifyContent: "center" }}>
                            {item.snapshot ? (
                              <img
                                src={item.snapshot}
                                alt={item.devicename}
                                style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                                onError={(e) => { e.target.style.display = "none"; if (e.target.nextSibling) e.target.nextSibling.style.display = "flex"; }}
                              />
                            ) : null}
                            <Box sx={{ display: item.snapshot ? "none" : "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", width: "100%", height: "100%" }}>
                              <VideocamOffIcon sx={{ fontSize: 20, color: UI.subtleIcon, mb: 0.25 }} />
                              <Typography sx={{ fontSize: 9, color: UI.textMuted }}>No Image</Typography>
                            </Box>
                          </Box>
                          <Box sx={{ px: 0.5, pt: 0.75, pb: 0.25 }}>
                            <Typography sx={{ fontSize: 12, fontWeight: 600, color: UI.textMain, lineHeight: 1.3 }}>{item.devicename}</Typography>
                            <Typography sx={{ fontSize: 10, color: UI.textMuted, mt: 0.25 }}>
                              {item.displayname || "\u2014"}{item.ServerName ? ` \u00b7 ${item.ServerName}` : ""}
                            </Typography>
                          </Box>
                        </Box>
                      );
                    })}
                    {configDevicesLoading && (
                      <Box sx={{ px: 1, py: 0.5 }}>
                        {[1, 2, 3].map(i => (
                          <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.75 }}>
                            <Skeleton variant="circular" width={28} height={28} />
                            <Box sx={{ flex: 1 }}>
                              <Skeleton variant="text" width="60%" height={14} />
                              <Skeleton variant="text" width="40%" height={12} />
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    )}
                    {!configDevicesLoading && configDevices.length === 0 && (
                      <Typography sx={{ fontSize: 11, color: UI.textMuted, textAlign: "center", py: 3 }}>No devices found</Typography>
                    )}
                  </Stack>
                </Box>
              </Paper>
            </Grid>

            {/* ── MIDDLE: Canvas + Header ── */}
            <Grid item xs={8} md={10} lg={12}>
              <Box sx={{ height: "100%", display: "flex", flexDirection: "column", position: "relative" }}>
                {/* Header */}
                <Box sx={{
                  px: 2, py: 1, backgroundColor: UI.panel, borderBottom: `1px solid ${UI.border}`,
                  display: "flex", alignItems: "center", justifyContent: "space-between", minHeight: 48,
                }}>
                  <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Typography variant="subtitle1" fontWeight={600}>
                      Configure: {configCombo?.display_name || configCombo?.combo_name || ""}
                    </Typography>
                    {configSelectedDevice && (
                      <Chip label={configSelectedDevice.devicename} size="small" variant="outlined" sx={{ height: 22, fontSize: 11 }} />
                    )}
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Button
                      size="small"
                      onClick={closeComboConfig}
                      sx={{
                        textTransform: "none", fontWeight: 600, fontSize: 12, px: 2, height: 32,
                        borderRadius: 1.5, color: UI.textMuted, backgroundColor: UI.inputBg, border: "none",
                        "&:hover": { backgroundColor: UI.hoverBg },
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      size="small"
                      disabled={configSaving || !configSelectedDevice?.deviceid}
                      onClick={handleComboConfigSave}
                      sx={{
                        textTransform: "none", fontWeight: 600, fontSize: 12, px: 2, height: 32,
                        borderRadius: 1.5, backgroundColor: UI.dark, color: UI.darkContrast,
                        "&:hover": { backgroundColor: theme.palette.primary.dark },
                        "&.Mui-disabled": { backgroundColor: UI.disabledBg, color: UI.disabledText },
                      }}
                    >
                      {configSaving ? <CircularProgress size={14} sx={{ color: UI.darkContrast }} /> : "Save"}
                    </Button>
                  </Stack>
                </Box>

                {/* Canvas area */}
                <Box sx={{ flex: 1, display: "flex", overflow: "hidden" }}>
                  <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", p: 2, backgroundColor: UI.surface }}>
                    {!configSelectedDevice ? (
                      <Stack alignItems="center" spacing={1}>
                        <VideocamOffIcon sx={{ fontSize: 40, color: UI.subtleIcon }} />
                        <Typography sx={{ fontSize: 13, color: UI.textMuted }}>Select a camera to configure</Typography>
                      </Stack>
                    ) : (
                      <Box sx={{ position: "relative", maxWidth: "100%", maxHeight: "100%" }}>
                        <img
                          src={configSelectedDevice.snapshot}
                          alt={configSelectedDevice.devicename}
                          style={{ maxWidth: "100%", maxHeight: "calc(100vh - 160px)", display: configImgBroken ? "none" : "block", borderRadius: 4 }}
                          onLoad={(e) => {
                            setConfigImgSize({ width: e.target.naturalWidth, height: e.target.naturalHeight });
                            const canvas = configCanvasRef.current;
                            if (canvas) {
                              canvas.width = e.target.clientWidth;
                              canvas.height = e.target.clientHeight;
                            }
                          }}
                          onError={() => setConfigImgBroken(true)}
                        />
                        {configImgBroken && (
                          <Stack alignItems="center" spacing={1} sx={{ py: 10 }}>
                            <VideocamOffIcon sx={{ fontSize: 40, color: UI.subtleIcon }} />
                            <Typography sx={{ fontSize: 12, color: UI.textMuted }}>No snapshot available</Typography>
                          </Stack>
                        )}
                        {!configImgBroken && (
                          <canvas
                            ref={configCanvasRef}
                            onMouseDown={handleConfigCanvasMouseDown}
                            onMouseMove={handleConfigCanvasMouseMove}
                            onMouseUp={handleConfigCanvasMouseUp}
                            style={{
                              position: "absolute", top: 0, left: 0,
                              width: "100%", height: "100%",
                              cursor: "crosshair", borderRadius: 4,
                            }}
                          />
                        )}
                        {configBoundary && (
                          <Tooltip title="Clear boundary">
                            <IconButton
                              size="small"
                              onClick={() => setConfigBoundary(null)}
                              sx={{
                                position: "absolute",
                                top: 8,
                                right: 8,
                                bgcolor: alpha(theme.palette.common.black, 0.5),
                                color: "common.white",
                                "&:hover": { bgcolor: alpha(theme.palette.common.black, 0.7) }
                              }}
                            >
                              <CloseIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    )}
                  </Box>

                  {/* ── RIGHT PANEL ── */}
                  <Box sx={{ width: 320, minWidth: 320, borderLeft: `1px solid ${UI.border}`, backgroundColor: UI.panel, overflowY: "auto" }} className="rp">
                    {!configSelectedDevice ? (
                      <Typography sx={{ fontSize: 12, color: UI.textMuted, textAlign: "center", py: 4 }}>Select a camera first</Typography>
                    ) : (
                      <>
                        {/* Combo Classes */}
                        <Accordion defaultExpanded>
                          <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: 16 }} />}>
                            <Typography variant="caption" fontWeight={600} sx={{ letterSpacing: 0.5 }}>COMBO CLASSES</Typography>
                          </AccordionSummary>
                          <AccordionDetails sx={{ pt: 0, px: 1.5 }}>
                            {(configCombo?.items || []).filter(it => it.class_id).length === 0 ? (
                              <Typography variant="caption" sx={{ color: UI.textMuted, display: "block", textAlign: "center", py: 1 }}>No classes in this combo.</Typography>
                            ) : (
                              <Stack spacing={0.5}>
                                {(configCombo?.items || []).filter(it => it.class_id).map((item) => {
                                  const isActive = configActiveClassId === item.class_id;
                                  const cn = getClassName(item.class_id);
                                  const modelAssigned = comboModels.find(m => m.class_id === item.class_id)?.model_version_id;
                                  return (
                                    <Box
                                      key={item.class_id}
                                      onClick={() => setConfigActiveClassId(item.class_id)}
                                      sx={{
                                        px: 1.25, py: 0.75, borderRadius: 1.5, cursor: "pointer",
                                        border: `1px solid ${isActive ? UI.accent : UI.border}`,
                                        backgroundColor: isActive ? UI.accentSoft : UI.panel,
                                        transition: "all 0.12s",
                                        "&:hover": { borderColor: UI.accent },
                                      }}
                                    >
                                      <Stack direction="row" alignItems="center" justifyContent="space-between">
                                        <Stack direction="row" alignItems="center" spacing={0.75}>
                                          <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: item.is_positive ? UI.green : theme.palette.error.main }} />
                                          <Typography variant="caption" fontWeight={600} sx={{ fontSize: 11 }}>{cn}</Typography>
                                        </Stack>
                                        <Stack direction="row" alignItems="center" spacing={0.5}>
                                          <Chip
                                            label={item.is_positive ? "YES" : "NO"}
                                            size="small"
                                            sx={{
                                              height: 16,
                                              fontSize: 9,
                                              fontWeight: 700,
                                              bgcolor: item.is_positive ? alpha(theme.palette.success.main, 0.1) : alpha(theme.palette.error.main, 0.1),
                                              color: item.is_positive ? UI.green : theme.palette.error.main
                                            }}
                                          />
                                          {modelAssigned ? (
                                            <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: UI.green }} />
                                          ) : (
                                            <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: UI.subtleIcon }} />
                                          )}
                                        </Stack>
                                      </Stack>
                                    </Box>
                                  );
                                })}
                              </Stack>
                            )}
                          </AccordionDetails>
                        </Accordion>

                        {/* Model Config */}
                        <Accordion defaultExpanded>
                          <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: 16 }} />}>
                            <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%">
                              <Typography variant="caption" fontWeight={600} sx={{ letterSpacing: 0.5 }}>MODEL CONFIG</Typography>
                            </Stack>
                          </AccordionSummary>
                          <AccordionDetails sx={{ pt: 0, px: 1.5 }}>
                            {!configActiveClassId ? (
                              <Typography variant="caption" sx={{ color: UI.textMuted, display: "block", textAlign: "center", py: 1 }}>Select a class above</Typography>
                            ) : (
                              <>
                                <Typography variant="caption" sx={{ color: UI.accent, fontSize: 10, display: "block", mb: 0.5 }}>
                                  {getClassName(configActiveClassId)}
                                </Typography>

                                {/* Model version selector */}
                                <Typography sx={{ fontSize: 10, color: UI.textMuted, mb: 0.5 }}>Model Version</Typography>
                                <Box sx={{ mb: 1 }}>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={(e) => setConfigModelPopoverAnchor(e.currentTarget)}
                                    sx={{
                                      textTransform: "none", fontSize: 11, justifyContent: "space-between", width: "100%",
                                      color: activeModelCfg?.model_version_id ? UI.dark : UI.textMuted,
                                      borderColor: UI.border,
                                    }}
                                  >
                                    {activeModelCfg?.model_version_label || activeModelCfg?.model_version_id
                                      ? (activeModelCfg.model_version_label || MODEL_OPTIONS.find(o => o.key === String(activeModelCfg.model_version_id))?.label || `ID: ${activeModelCfg.model_version_id}`)
                                      : "Select model..."}
                                  </Button>
                                </Box>

                                {/* Model picker popover */}
                                <Popover
                                  open={Boolean(configModelPopoverAnchor)}
                                  anchorEl={configModelPopoverAnchor}
                                  onClose={() => setConfigModelPopoverAnchor(null)}
                                  anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                                >
                                  <Box sx={{
                                    ...POP_LIST_SX,
                                    width: 220, maxHeight: 300, overflowY: "auto", px: 0.5, py: 0.5,
                                    "&::-webkit-scrollbar": { width: 4 },
                                    "&::-webkit-scrollbar-track": { background: "transparent" },
                                    "&::-webkit-scrollbar-thumb": { backgroundColor: UI.scrollThumb, borderRadius: 4 },
                                  }}>
                                    {MODEL_GROUPS.length === 0 ? (
                                      <Typography sx={{ fontSize: 11, color: alpha(theme.palette.common.white, 0.5), textAlign: "center", py: 2 }}>No models available</Typography>
                                    ) : MODEL_GROUPS.map((group, gi) => (
                                      <Box key={group.header}>
                                        <Typography sx={{ fontSize: 10, fontWeight: 700, color: alpha(theme.palette.common.white, 0.4), textTransform: "uppercase", letterSpacing: 0.5, px: 1, pt: gi === 0 ? 0.75 : 0.75, pb: 0.25 }}>{group.header}</Typography>
                                        {group.models.map(m => {
                                          const isSelected = activeModelCfg?.model_version_id === Number(m.key);
                                          return (
                                            <ListItemButton key={m.key} dense onClick={() => selectModelVersion(m.key)} sx={{ borderRadius: 1, px: 1, py: 0.25, backgroundColor: isSelected ? alpha(theme.palette.common.white, 0.1) : "transparent" }}>
                                              <ListItemText primary={m.label} primaryTypographyProps={{ fontSize: 11, color: UI.textMain, fontWeight: isSelected ? 700 : 400 }} />
                                            </ListItemButton>
                                          );
                                        })}
                                        {gi < MODEL_GROUPS.length - 1 && <Divider sx={{ my: 0.5, borderColor: alpha(theme.palette.common.white, 0.08) }} />}
                                      </Box>
                                    ))}
                                  </Box>
                                </Popover>

                                {activeModelCfg?.model_version_id && (
                                  <>
                                    {/* Sliders */}
                                    <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", columnGap: 2, rowGap: 1, mb: 1 }}>
                                      {[
                                        { label: "Confidence", field: "conf", min: 0.1, max: 1, step: 0.05 },
                                        { label: "IoU", field: "iou", min: 0.1, max: 1, step: 0.05 },
                                        { label: "Detect FPS", field: "detect_fps", min: 1, max: 30, step: 1 },
                                      ].map(s => (
                                        <Box key={s.field}>
                                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                                            <Typography sx={{ fontSize: 10, color: UI.textMuted }}>{s.label}</Typography>
                                            <Typography sx={{ fontSize: 10, fontWeight: 700, color: UI.textMain }}>{activeModelCfg[s.field]}</Typography>
                                          </Stack>
                                          <Slider size="small" value={activeModelCfg[s.field]} min={s.min} max={s.max} step={s.step} onChange={(_, v) => updateActiveModelField(s.field, v)} sx={{ py: 0.5 }} />
                                        </Box>
                                      ))}
                                    </Box>

                                    {/* Switches */}
                                    <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 0.5, mb: 1 }}>
                                      {[
                                        { key: "agnostic_nms", label: "Agnostic NMS" },
                                        { key: "is_zoom_motion_area", label: "Zoom Motion" },
                                      ].map(opt => (
                                        <FormControlLabel
                                          key={opt.key}
                                          control={<Switch size="small" checked={activeModelCfg[opt.key]} onChange={(e) => updateActiveModelField(opt.key, e.target.checked)} />}
                                          label={<Typography sx={{ fontSize: 10 }}>{opt.label}</Typography>}
                                          sx={{ ml: 0, mr: 0 }}
                                          labelPlacement="start"
                                        />
                                      ))}
                                    </Box>

                                    {/* Model Format */}
                                    <Divider sx={{ my: 1 }} />
                                    <Typography sx={{ fontSize: 11, color: UI.textMuted, mb: 0.5 }}>Model Format</Typography>
                                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 1 }}>
                                      {MODEL_FORMATS.map(f => (
                                        <Chip
                                          key={f.key}
                                          label={f.label}
                                          size="small"
                                          onClick={() => updateActiveModelField("model_format", f.key)}
                                          sx={{
                                            height: 22, fontSize: 10, fontWeight: 600, cursor: "pointer",
                                            backgroundColor: activeModelCfg.model_format === f.key ? UI.dark : UI.hoverBg,
                                            color: activeModelCfg.model_format === f.key ? UI.darkContrast : UI.textMuted,
                                          }}
                                        />
                                      ))}
                                    </Box>

                                    {/* Communication */}
                                    <Divider sx={{ my: 1 }} />
                                    <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
                                      <Typography variant="caption" fontWeight={600} sx={{ fontSize: 11 }}>Communication</Typography>
                                      <WifiIcon sx={{ fontSize: 13, color: UI.textMuted }} />
                                    </Stack>
                                    <Box sx={{ mb: 1 }}>
                                      <Typography sx={{ fontSize: 11, color: UI.textMuted, mb: 0.5 }}>Protocol</Typography>
                                      <Box sx={{ display: "flex", borderRadius: 1, border: `1px solid ${UI.border}`, overflow: "hidden", height: 28 }}>
                                        {["grpc", "http"].map(type => (
                                          <Box
                                            key={type}
                                            onClick={() => updateActiveModelField("communication_type", type)}
                                            sx={{
                                              flex: 1, display: "flex", alignItems: "center", justifyContent: "center",
                                              fontSize: 10, fontWeight: 600, cursor: "pointer", transition: "all 0.15s",
                                              backgroundColor: activeModelCfg.communication_type === type ? UI.dark : "transparent",
                                              color: activeModelCfg.communication_type === type ? "common.white" : UI.textMuted,
                                              "&:hover": { backgroundColor: activeModelCfg.communication_type === type ? UI.dark : alpha(theme.palette.common.black, 0.03) },
                                            }}
                                          >
                                            {type.toUpperCase()}
                                          </Box>
                                        ))}
                                      </Box>
                                    </Box>

                                    {activeModelCfg.communication_type === "grpc" ? (
                                      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", columnGap: 2, rowGap: 1 }}>
                                        <Box>
                                          <Typography sx={{ fontSize: 11, color: UI.textMuted, mb: 0.5 }}>IP Address</Typography>
                                          <TextField size="small" fullWidth placeholder="127.0.0.1" value={activeModelCfg.grpc_connect_ip || ""} onChange={(e) => updateActiveModelField("grpc_connect_ip", e.target.value)} InputProps={{ startAdornment: <InputAdornment position="start"><SettingsEthernetIcon sx={{ fontSize: 12, color: UI.textMuted }} /></InputAdornment> }} />
                                        </Box>
                                        <Box>
                                          <Typography sx={{ fontSize: 11, color: UI.textMuted, mb: 0.5 }}>Port</Typography>
                                          <TextField size="small" fullWidth placeholder="50051" value={activeModelCfg.grpc_connect_port || ""} onChange={(e) => updateActiveModelField("grpc_connect_port", Number(e.target.value) || 0)} InputProps={{ startAdornment: <InputAdornment position="start"><NumbersIcon sx={{ fontSize: 12, color: UI.textMuted }} /></InputAdornment> }} />
                                        </Box>
                                      </Box>
                                    ) : (
                                      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", columnGap: 2, rowGap: 1 }}>
                                        <Box>
                                          <Typography sx={{ fontSize: 11, color: UI.textMuted, mb: 0.5 }}>URL</Typography>
                                          <TextField size="small" fullWidth placeholder="https://api.example.com" value={activeModelCfg.http_private_base_url || ""} onChange={(e) => updateActiveModelField("http_private_base_url", e.target.value)} InputProps={{ startAdornment: <InputAdornment position="start"><SettingsEthernetIcon sx={{ fontSize: 12, color: UI.textMuted }} /></InputAdornment> }} />
                                        </Box>
                                        <Box>
                                          <Typography sx={{ fontSize: 11, color: UI.textMuted, mb: 0.5 }}>Path</Typography>
                                          <TextField size="small" fullWidth placeholder="/v1/detect" value={activeModelCfg.http_path || ""} onChange={(e) => updateActiveModelField("http_path", e.target.value)} />
                                        </Box>
                                      </Box>
                                    )}
                                  </>
                                )}
                              </>
                            )}
                          </AccordionDetails>
                        </Accordion>

                        {/* Tracking */}
                        <Accordion>
                          <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: 16 }} />}>
                            <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%">
                              <Typography variant="caption" fontWeight={600} sx={{ letterSpacing: 0.5 }}>TRACKING</Typography>
                              <Switch
                                size="small"
                                checked={comboTrackingEnabled}
                                onChange={(e) => { e.stopPropagation(); setComboTrackingEnabled(e.target.checked); }}
                                onClick={(e) => e.stopPropagation()}
                                sx={{ mr: 0.5 }}
                              />
                            </Stack>
                          </AccordionSummary>
                          <AccordionDetails sx={{ pt: 0, px: 1.5 }}>
                            {!comboTrackingEnabled ? (
                              <Typography variant="caption" sx={{ color: UI.textMuted, display: "block", textAlign: "center", py: 1 }}>Tracking disabled</Typography>
                            ) : (
                              <>
                                <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", columnGap: 2, rowGap: 1, mb: 1 }}>
                                  {[
                                    { label: "Track IoU", field: "track_iou", min: 0.1, max: 1, step: 0.05 },
                                    { label: "Max Disappear", field: "track_max_disappeared", min: 5, max: 100, step: 5 },
                                    { label: "Tracking FPS", field: "tracking_fps", min: 1, max: 30, step: 1 },
                                    { label: "Min Distance", field: "min_track_distance", min: 10, max: 200, step: 5 },
                                    { label: "Min Duration", field: "min_track_duration_sec", min: 1, max: 30, step: 1 },
                                  ].map(s => (
                                    <Box key={s.field}>
                                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                                        <Typography sx={{ fontSize: 10, color: UI.textMuted }}>{s.label}</Typography>
                                        <Typography sx={{ fontSize: 10, fontWeight: 700, color: UI.textMain }}>{comboTracking[s.field]}</Typography>
                                      </Stack>
                                      <Slider size="small" value={comboTracking[s.field]} min={s.min} max={s.max} step={s.step} onChange={(_, v) => updateTracking(s.field, v)} sx={{ py: 0.5, color: UI.dark }} />
                                    </Box>
                                  ))}
                                </Box>

                                <FormControlLabel
                                  control={<Switch size="small" checked={comboTracking.enable_object_count} onChange={(e) => updateTracking("enable_object_count", e.target.checked)} />}
                                  label={<Typography sx={{ fontSize: 10 }}>Enable Object Count</Typography>}
                                  sx={{ ml: 0, mr: 0, mb: 0.5 }}
                                  labelPlacement="start"
                                />

                                {comboTracking.enable_object_count && (
                                  <Box sx={{ p: 1, borderRadius: 1.5, border: `1px solid ${UI.border}`, backgroundColor: UI.surface }}>
                                    <Typography sx={{ fontSize: 11, color: UI.textMuted, mb: 0.5 }}>Track Direction</Typography>
                                    <Box sx={{ display: "flex", borderRadius: 1, border: `1px solid ${UI.border}`, overflow: "hidden", height: 28 }}>
                                      {[
                                        { key: "TTB", icon: <ArrowDownwardIcon sx={{ fontSize: 14 }} />, tip: "Top to Bottom" },
                                        { key: "BTT", icon: <ArrowUpwardIcon sx={{ fontSize: 14 }} />, tip: "Bottom to Top" },
                                        { key: "LTR", icon: <ArrowForwardIcon sx={{ fontSize: 14 }} />, tip: "Left to Right" },
                                        { key: "RTL", icon: <ArrowBackIcon sx={{ fontSize: 14 }} />, tip: "Right to Left" },
                                      ].map(opt => (
                                        <Tooltip key={opt.key} title={opt.tip} placement="top" arrow>
                                          <Box
                                            onClick={() => updateTracking("track_direction", opt.key)}
                                            sx={{
                                              flex: 1, display: "flex", alignItems: "center", justifyContent: "center",
                                              cursor: "pointer", transition: "all 0.15s",
                                              backgroundColor: comboTracking.track_direction === opt.key ? UI.dark : "transparent",
                                              color: comboTracking.track_direction === opt.key ? "common.white" : UI.textMuted,
                                              "&:hover": { backgroundColor: comboTracking.track_direction === opt.key ? UI.dark : alpha(theme.palette.common.black, 0.03) },
                                            }}
                                          >
                                            {opt.icon}
                                          </Box>
                                        </Tooltip>
                                      ))}
                                    </Box>
                                  </Box>
                                )}
                              </>
                            )}
                          </AccordionDetails>
                        </Accordion>

                        {/* Monitoring Times */}
                        <Accordion>
                          <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: 16 }} />}>
                            <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%">
                              <Typography variant="caption" fontWeight={600} sx={{ letterSpacing: 0.5 }}>MONITORING TIMES</Typography>
                              <IconButton size="small" onClick={(e) => { e.stopPropagation(); addMonitoringTimeRange(); }} sx={{ p: 0.25, mr: 0.5 }}>
                                <AddIcon sx={{ fontSize: 14, color: UI.accent }} />
                              </IconButton>
                            </Stack>
                          </AccordionSummary>
                          <AccordionDetails sx={{ pt: 0, px: 1.5 }}>
                            {comboMonitoringTimes.length === 0 && (
                              <Typography variant="caption" sx={{ color: UI.textMuted, display: "block", textAlign: "center", py: 1 }}>No time ranges configured.</Typography>
                            )}

                            {comboMonitoringTimes.map((range, idx) => (
                              <Box key={idx} sx={{ mb: 1.5, p: 1.5, border: `1px solid ${UI.border}`, borderRadius: 2, backgroundColor: UI.panel }}>
                                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.75}>
                                  <Typography sx={{ fontSize: 11, fontWeight: 600 }}>Range {idx + 1}</Typography>
                                  <IconButton size="small" onClick={() => removeMonitoringTimeRange(idx)} sx={{ p: 0.25 }}>
                                    <DeleteIcon sx={{ fontSize: 13, color: UI.textMuted }} />
                                  </IconButton>
                                </Stack>

                                <Stack direction="row" spacing={0.75} mb={1} alignItems="center">
                                  <input type="time" value={range.startTime} onChange={(e) => updateMonitoringTimeRange(idx, "startTime", e.target.value)} style={{ fontSize: 11, padding: "3px 6px", flex: 1, border: `1px solid ${UI.border}`, borderRadius: 4, background: UI.surface, color: UI.textMain, outline: "none" }} />
                                  <Typography sx={{ fontSize: 10, color: UI.textMuted }}>{"\u2013"}</Typography>
                                  <input type="time" value={range.endTime} onChange={(e) => updateMonitoringTimeRange(idx, "endTime", e.target.value)} style={{ fontSize: 11, padding: "3px 6px", flex: 1, border: `1px solid ${UI.border}`, borderRadius: 4, background: UI.surface, color: UI.textMain, outline: "none" }} />
                                </Stack>

                                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                  {DAYS.map(day => (
                                    <Chip
                                      key={day}
                                      label={day}
                                      size="small"
                                      onClick={() => toggleMonitoringDay(idx, day)}
                                      sx={{
                                        cursor: "pointer", fontSize: 10, height: 22, fontWeight: 600,
                                        backgroundColor: range.days.includes(day) ? UI.accent : "transparent",
                                        color: range.days.includes(day) ? UI.darkContrast : UI.textMuted,
                                        border: `1px solid ${range.days.includes(day) ? UI.accent : UI.border}`,
                                      }}
                                    />
                                  ))}
                                </Box>
                              </Box>
                            ))}
                          </AccordionDetails>
                        </Accordion>
                      </>
                    )}
                  </Box>
                </Box>
              </Box>
            </Grid>

          </Grid>
        </Box>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={3000} onClose={() => setSnack(s => ({ ...s, open: false }))} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert onClose={() => setSnack(s => ({ ...s, open: false }))} severity={snack.severity} variant="filled" sx={{ borderRadius: 1 }}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Page>
  );
}
